import React from "react";
import { Link } from "@inertiajs/react";
import styled from "styled-components";
import { narrowScreenMediaQuery, brand_gradient } from "../constants/styles";
import { LoggedInUserContext } from "../providers/ProvideLoggedInUser";
import useDropdown from "./useDropdown";
import {
  BuildingIcon,
  DashboardIcon,
  FileTextIcon,
  IdCardIcon,
  LoginIcon,
  LogoutIcon,
  ShareIcon,
  ShieldIcon,
  UserIcon,
} from "./MenuIcons";

// Regrouped portal-aware menu — see Phase D plan. The dropdown is now
// a digest of the user's portal rather than a flat link list. Logical
// subgroups: Portal CTA → Akses Cepat → Untuk Perusahaan → Penghasilan
// → Admin → Logout. Each subgroup is omitted entirely when no items
// inside it are visible to the current user, so a plain user only ever
// sees the lean baseline.
const buildMenuGroups = (signedIn, roles) => {
  if (!signedIn) {
    return [
      {
        key: "guest",
        items: [
          { key: "login", name: "Login", link: "/login", Icon: LoginIcon },
        ],
      },
    ];
  }

  const has = (r) => Array.isArray(roles) && roles.includes(r);

  const groups = [];

  // Hero CTA — primary path into the portal home.
  groups.push({
    key: "portal",
    primary: {
      key: "portal",
      name: "Buka Portal Saya",
      link: "/dashboards",
      Icon: DashboardIcon,
    },
  });

  // Quick-access (always for signed-in users).
  groups.push({
    key: "akses",
    label: "Akses Cepat",
    items: [
      {
        key: "sertifikat",
        name: "Sertifikat",
        link: "/sertifikat",
        Icon: IdCardIcon,
      },
      {
        key: "membership",
        name: "Membership",
        link: "/memberships",
        Icon: IdCardIcon,
      },
      { key: "profile", name: "Profil", link: "/profiles", Icon: UserIcon },
    ],
  });

  // Company-only group.
  const companyItems = [];
  if (has("admin_company")) {
    companyItems.push({
      key: "admin_company",
      name: "Dashboard Perusahaan",
      link: "/admin_company/dashboard",
      Icon: BuildingIcon,
      enterprise: true,
    });
  }
  if (has("user_company")) {
    companyItems.push({
      key: "user_company",
      name: "Laporan",
      link: "/admin_company/reports",
      Icon: FileTextIcon,
      enterprise: true,
    });
  }
  if (companyItems.length > 0) {
    groups.push({
      key: "perusahaan",
      label: "Untuk Perusahaan",
      items: companyItems,
    });
  }

  // Affiliator — always visible for signed-in users.
  const isAffiliator = has("affiliator");
  groups.push({
    key: "penghasilan",
    label: "Penghasilan",
    items: [
      {
        key: "affiliator",
        name: "Afiliator",
        link: isAffiliator ? "/users/affiliators" : "/affiliators",
        Icon: ShareIcon,
        promo: !isAffiliator,
        promoLabel: "Gabung!",
      },
    ],
  });

  // Admin-only system controls.
  if (has("admin")) {
    groups.push({
      key: "system",
      label: "Sistem",
      items: [
        {
          key: "admin",
          name: "Admin Panel",
          link: "/admin/dashboard",
          Icon: ShieldIcon,
        },
      ],
    });
  }

  return groups;
};

function UserProfileMenu() {
  const { signedIn, currentUser, performLogout } =
    React.use(LoggedInUserContext);
  const { open, toggle, close, rootRef } = useDropdown();

  const name = signedIn ? currentUser?.name || currentUser?.email || "U" : "U";
  const menuGroups = React.useMemo(
    () => buildMenuGroups(signedIn, currentUser?.roles),
    [signedIn, currentUser?.roles],
  );

  const handleLogout = React.useCallback(async () => {
    close();
    await performLogout();
    // performLogout only clears React state; on a server-rendered page (e.g.
    // /profiles) that leaves logged-in content on screen. Full-navigate home
    // so the server re-renders the anonymous homepage.
    window.location.href = "/";
  }, [close, performLogout]);

  return (
    <Root ref={rootRef}>
      <Trigger
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu profil"
      >
        <Avatar
          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(name)}${
            signedIn ? "&background=d85907&color=ffffff&bold=true" : ""
          }`}
          alt=""
        />
        <Chevron $open={open} aria-hidden="true" />
      </Trigger>

      {open && (
        <Panel role="menu">
          {signedIn && currentUser && (
            <PanelHeader>
              <Name>{currentUser.name || currentUser.email}</Name>
              {currentUser.name && <Email>{currentUser.email}</Email>}
            </PanelHeader>
          )}
          <Groups>
            {menuGroups.map((g) =>
              g.primary ? (
                <PrimaryItemLink
                  key={g.primary.key}
                  href={g.primary.link}
                  role="menuitem"
                  onClick={close}
                >
                  <ItemIcon>
                    <g.primary.Icon />
                  </ItemIcon>
                  <ItemText>{g.primary.name}</ItemText>
                  <PrimaryChevron aria-hidden="true">→</PrimaryChevron>
                </PrimaryItemLink>
              ) : (
                <Group key={g.key}>
                  {g.label && <GroupLabel>{g.label}</GroupLabel>}
                  <ItemList>
                    {g.items.map(
                      ({
                        key,
                        name: label,
                        link,
                        Icon,
                        promo,
                        promoLabel,
                        enterprise,
                      }) => {
                        if (enterprise) {
                          return (
                            <EnterpriseItemLink
                              key={key}
                              href={link}
                              role="menuitem"
                              onClick={close}
                            >
                              <ItemIcon>
                                <Icon />
                              </ItemIcon>
                              <ItemText>{label}</ItemText>
                              <EnterpriseBadge>PRO</EnterpriseBadge>
                            </EnterpriseItemLink>
                          );
                        }
                        if (promo) {
                          return (
                            <ItemLink
                              key={key}
                              href={link}
                              role="menuitem"
                              onClick={close}
                            >
                              <ItemIcon>
                                <Icon />
                              </ItemIcon>
                              <GradientText>{label}</GradientText>
                              {promoLabel && (
                                <PromoBadge>{promoLabel}</PromoBadge>
                              )}
                            </ItemLink>
                          );
                        }
                        return (
                          <ItemLink
                            key={key}
                            href={link}
                            role="menuitem"
                            onClick={close}
                          >
                            <ItemIcon>
                              <Icon />
                            </ItemIcon>
                            <ItemText>{label}</ItemText>
                          </ItemLink>
                        );
                      },
                    )}
                  </ItemList>
                </Group>
              ),
            )}
            {signedIn && (
              <>
                <Separator />
                <ItemList>
                  <ItemButton
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    <ItemIcon>
                      <LogoutIcon />
                    </ItemIcon>
                    <ItemText>Logout</ItemText>
                  </ItemButton>
                </ItemList>
              </>
            )}
          </Groups>
        </Panel>
      )}
    </Root>
  );
}
export default React.memo(UserProfileMenu);

// ---- styles -------------------------------------------------------------

const Root = styled.div`
  position: relative;
  display: inline-flex;
`;

const Trigger = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  padding: 4px 8px 4px 4px;
  transition: background 120ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  [data-theme="dark"] &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Avatar = styled.img`
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: block;
`;

const Chevron = styled.span`
  display: inline-block;
  margin-left: 6px;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid currentColor;
  color: #6b7280;
  transition: transform 180ms ease;
  transform: ${({ $open }) => ($open ? "rotate(-180deg)" : "none")};

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Panel = styled.div`
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  color: #1f2937;
  cursor: default;
  display: flex;
  flex-direction: column;
  min-width: 220px;
  max-width: calc(100vw - 24px);
  padding: 8px 0;
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  text-align: left;
  z-index: 50;
  animation: fadeIn 140ms ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* On narrow screens the trigger sits mid-header (with burger to its right),
   * so anchoring the panel to the trigger's right edge causes the left edge
   * to overflow the viewport. Switch to viewport-fixed anchoring. */
  @media ${narrowScreenMediaQuery} {
    position: fixed;
    top: 66px;
    right: 10px;
    left: auto;
    width: 260px;
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 80px);
    overflow-y: auto;
  }

  [data-theme="dark"] & {
    background-color: #17171e;
    border-color: rgba(255, 255, 255, 0.08);
    color: #f3f4f6;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  }
`;

const PanelHeader = styled.div`
  background: linear-gradient(90deg, #d85907 0%, #f7b30c 100%);
  border-radius: 8px;
  color: #ffffff;
  margin: 4px 12px 8px;
  padding: 10px 12px;
`;

const Name = styled.div`
  font-weight: 700;
  font-size: 14px;
  overflow-wrap: anywhere;
`;

const Email = styled.div`
  font-size: 11px;
  opacity: 0.92;
  margin-top: 2px;
  overflow-wrap: anywhere;
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 4px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
`;

const GroupLabel = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9ca3af;
  padding: 4px 18px 2px;

  [data-theme="dark"] & {
    color: #6b7280;
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 8px;
`;

const itemBaseStyle = `
  align-items: center;
  background: none;
  border: none;
  border-radius: 8px;
  color: inherit;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 13px;
  gap: 10px;
  padding: 8px 10px;
  text-align: left;
  text-decoration: none;
  transition: background 120ms ease, color 120ms ease;
  width: 100%;
`;

const ItemLink = styled(Link)`
  ${itemBaseStyle}

  &:hover {
    background: #fff7ed;
    color: #d85907;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  [data-theme="dark"] &:hover {
    background: rgba(216, 89, 7, 0.18);
    color: #fdba74;
  }
`;

const EnterpriseItemLink = styled(Link)`
  ${itemBaseStyle}

  &:hover {
    background: rgba(42, 159, 214, 0.08);
    color: #2a9fd6;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid #2a9fd6;
    outline-offset: 2px;
  }

  [data-theme="dark"] &:hover {
    background: rgba(42, 159, 214, 0.18);
    color: #7dd3fc;
  }
`;

const PrimaryItemLink = styled(Link)`
  ${itemBaseStyle}
  background: linear-gradient(90deg, #d85907 0%, #f7b30c 100%);
  color: #ffffff;
  font-weight: 800;
  margin: 0 12px 4px;
  padding: 12px 14px;
  width: auto;
  box-shadow: 0 6px 14px rgba(216, 89, 7, 0.28);

  &:hover {
    color: #ffffff;
    text-decoration: none;
    filter: brightness(0.97);
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
`;

const PrimaryChevron = styled.span`
  margin-left: auto;
  font-weight: 800;
`;

const ItemButton = styled.button`
  ${itemBaseStyle}

  &:hover {
    background: #fff7ed;
    color: #d85907;
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  [data-theme="dark"] &:hover {
    background: rgba(216, 89, 7, 0.18);
    color: #fdba74;
  }
`;

const ItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: #6b7280;
  flex-shrink: 0;
  transition: color 120ms ease;

  ${ItemLink}:hover &,
  ${ItemButton}:hover & {
    color: #d85907;
  }

  ${EnterpriseItemLink}:hover & {
    color: #2a9fd6;
  }

  ${PrimaryItemLink} & {
    color: #ffffff;
  }

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const ItemText = styled.span``;

const GradientText = styled.span`
  background: ${brand_gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const PromoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${brand_gradient};
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
`;

const EnterpriseBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(42, 159, 214, 0.12);
  color: #2a9fd6;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
`;

const Separator = styled.div`
  background-color: #e5e7eb;
  height: 1px;
  margin: 8px 4px;

  [data-theme="dark"] & {
    background-color: rgba(255, 255, 255, 0.08);
  }
`;
